import React, { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import {
  Container, Typography, Box, Button, Paper, Card, Avatar, Chip,
  TextField, MenuItem, Select, FormControl, InputLabel, Stack,
  Autocomplete
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

interface Company {
  id: number
  name: string
}

interface Group {
  id: number
  name: string
  company: Company
  gender: string
  group_type: string
  member_count: number
  created_at: string
}

interface SearchableGroup {
  id: number
  name: string
  company: string
}

interface Props {
  groups: Group[]
  companies: Company[]
  all_groups: SearchableGroup[]
  filters: {
    q: string | null
    gender: string | null
    group_type: string | null
    company_id: string | null
    ids: number[] | null
  }
}

const genderLabels: Record<string, string> = {
  girl: 'Girl',
  boy: 'Boy',
  mixed: 'Mixed',
}

const groupTypeLabels: Record<string, string> = {
  group: 'Group',
  band: 'Band',
}

export default function GroupsIndex({ groups, companies, all_groups, filters }: Props) {
  const [gender, setGender] = useState(filters.gender || '')
  const [groupType, setGroupType] = useState(filters.group_type || '')
  const [companyId, setCompanyId] = useState(filters.company_id || '')
  const initialSelectedIds = (filters.ids || []).map(Number)
  const [selectedGroups, setSelectedGroups] = useState<SearchableGroup[]>(
    all_groups.filter((g) => initialSelectedIds.includes(g.id))
  )

  const apply = (overrides: Record<string, unknown> = {}) => {
    const params: Record<string, unknown> = {
      gender: gender || undefined,
      group_type: groupType || undefined,
      company_id: companyId || undefined,
      ids: selectedGroups.map((g) => g.id),
      ...overrides,
    }
    if (Array.isArray(params.ids) && (params.ids as number[]).length === 0) {
      delete params.ids
    }
    router.get('/groups', params as Record<string, string>, { preserveState: true })
  }

  const handleSelectionChange = (_: unknown, value: SearchableGroup[]) => {
    setSelectedGroups(value)
    const params: Record<string, unknown> = {
      gender: gender || undefined,
      group_type: groupType || undefined,
      company_id: companyId || undefined,
      ids: value.map((g) => g.id),
    }
    if ((params.ids as number[]).length === 0) delete params.ids
    router.get('/groups', params as Record<string, string>, { preserveState: true })
  }

  return (
    <>
      <Head title="Groups" />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5">Groups</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {groups.length} {groups.length === 1 ? 'group' : 'groups'}
            </Typography>
          </Box>
          <Button component={Link} href="/groups/new" variant="contained" startIcon={<AddIcon />}>
            Add Group
          </Button>
        </Box>

        <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 3, boxShadow: '0 2px 12px rgba(123, 91, 214,0.08)' }}>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
            <Autocomplete
              multiple
              size="small"
              options={all_groups}
              getOptionLabel={(option) => option.name}
              value={selectedGroups}
              onChange={handleSelectionChange}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Groups"
                  placeholder="Type to search…"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.company}</Typography>
                  </Box>
                </li>
              )}
              sx={{ minWidth: 280, flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Gender</InputLabel>
              <Select
                value={gender}
                label="Gender"
                onChange={(e) => { setGender(e.target.value); apply({ gender: e.target.value || undefined }) }}
                sx={{ borderRadius: 999 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="girl">Girl</MenuItem>
                <MenuItem value="boy">Boy</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={groupType}
                label="Type"
                onChange={(e) => { setGroupType(e.target.value); apply({ group_type: e.target.value || undefined }) }}
                sx={{ borderRadius: 999 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="group">Group</MenuItem>
                <MenuItem value="band">Band</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Company</InputLabel>
              <Select
                value={companyId}
                label="Company"
                onChange={(e) => { setCompanyId(e.target.value); apply({ company_id: e.target.value || undefined }) }}
                sx={{ borderRadius: 999 }}
              >
                <MenuItem value="">All Companies</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {groups.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No groups found.{' '}
              <Link href="/groups/new" style={{ color: '#7B5BD6', fontWeight: 600 }}>Add your first group →</Link>
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
              },
            }}
          >
            {groups.map((group) => (
              <Card
                key={group.id}
                component={Link}
                href={`/groups/${group.id}`}
                sx={{
                  p: 2,
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  textAlign: 'center',
                  transition: 'transform 0.15s ease',
                  '&:hover': { transform: 'translateY(-2px)' },
                }}
              >
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    backgroundColor: 'rgba(123, 91, 214,0.15)',
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: '1.6rem',
                  }}
                >
                  {group.name.charAt(0)}
                </Avatar>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5 }}>{group.name}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {group.company.name}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
                  <Chip label={genderLabels[group.gender] || group.gender} size="small" />
                  <Chip label={groupTypeLabels[group.group_type] || group.group_type} size="small" variant="outlined" />
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                </Typography>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </>
  )
}
